import { useMutation, useQuery } from '@tanstack/react-query'
import Card from 'react-bootstrap/Card'
import Col from 'react-bootstrap/Col'
import FloatingLabel from 'react-bootstrap/FloatingLabel'
import Form from 'react-bootstrap/Form'
import Row from 'react-bootstrap/Row'
import { fetchLabBaseAuthUserDetails, redirectToLabBaseAuthPage } from '@/util/auth'
import { fetchUserData, PersonalInfoSchema, saveUserData, DEFAULT_PERSONAL_INFO } from './data'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { useEffect, useState } from 'react'
import { cursorPointer, readOnlyMode } from './style.css'
import { UserIcon } from 'lucide-react'
import clsx from 'clsx'
import { SquarePen } from 'lucide-react'
import { queryClient } from '@/util/queryClient'
import Button from 'react-bootstrap/Button'
import Stack from 'react-bootstrap/Stack'

export default function PersonalInfoPanel() {
  const { data: lbUserDetails } = useQuery({
    queryKey: ['lb-auth-user-details'],
    queryFn: fetchLabBaseAuthUserDetails,
    staleTime: 1000 * 60 * 60,
  })

  const { data: userData } = useQuery({
    queryKey: ['user', 'profile'],
    queryFn: fetchUserData,
    staleTime: 1000 * 60 * 60,
  })

  const userInfoMutation = useMutation({
    mutationFn: saveUserData,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['user', 'profile'] })
      setEditMode(false)
    },
    onError: (err) => console.log(err),
  })

  const {
    register,
    setValue,
    formState: { errors },
    handleSubmit,
  } = useForm({
    resolver: zodResolver(PersonalInfoSchema),
    values: userData || DEFAULT_PERSONAL_INFO,
    mode: 'onChange',
  })

  const [editMode, setEditMode] = useState<boolean>(false)

  useEffect(() => {
    if (lbUserDetails) setValue('email', lbUserDetails.emailAddress)
  }, [lbUserDetails, setValue])

  return (
    <Card>
      <Card.Body>
        <Card.Title className="d-flex flex-row justify-content-between align-items-center">
          <div>
            <UserIcon className="me-1" />
            基本情報
          </div>
          {!editMode && <SquarePen className={cursorPointer} onClick={() => setEditMode(true)} />}
        </Card.Title>
        <Row>
          <Col>
            <FloatingLabel label="姓" className="mb-3">
              <Form.Control
                {...register('lastName')}
                type="text"
                placeholder="Lab"
                isInvalid={!!errors.lastName}
                readOnly={!editMode}
                className={clsx(!editMode && readOnlyMode)}
              />
            </FloatingLabel>
          </Col>
          <Col>
            <FloatingLabel label="名" className="mb-3">
              <Form.Control
                {...register('firstName')}
                type="text"
                placeholder="任三郎"
                isInvalid={!!errors.firstName}
                readOnly={!editMode}
                className={clsx(!editMode && readOnlyMode)}
              />
            </FloatingLabel>
          </Col>
        </Row>
        <Row>
          <Col>
            <FloatingLabel label="姓（フリガナ）" className="mb-3">
              <Form.Control
                {...register('lastNameFurigana')}
                type="text"
                placeholder="ラブ"
                isInvalid={!!errors.lastNameFurigana}
                readOnly={!editMode}
                className={clsx(!editMode && readOnlyMode)}
              />
            </FloatingLabel>
          </Col>
          <Col>
            <FloatingLabel label="名（フリガナ）" className="mb-3">
              <Form.Control
                {...register('firstNameFurigana')}
                type="text"
                placeholder="ニンザブロウ"
                isInvalid={!!errors.firstNameFurigana}
                readOnly={!editMode}
                className={clsx(!editMode && readOnlyMode)}
              />
            </FloatingLabel>
          </Col>
        </Row>
        <Row>
          <Col>
            <FloatingLabel label="生年月日" className="mb-3">
              <Form.Control
                {...register('birthdate')}
                type="date"
                placeholder="2015/03/21"
                isInvalid={!!errors.birthdate}
                readOnly={!editMode}
                className={clsx(!editMode && readOnlyMode)}
              />
            </FloatingLabel>
          </Col>
        </Row>
        <Row>
          <Col>
            <FloatingLabel label="メール" className="mb-3">
              <Form.Control
                type="email"
                value={lbUserDetails?.emailAddress ?? ''}
                placeholder="name@example.com"
                readOnly
                onClick={() =>
                  editMode && redirectToLabBaseAuthPage('/user/profile', 'update/email')
                }
                className={clsx(editMode && cursorPointer, !editMode && readOnlyMode)}
              />
            </FloatingLabel>
          </Col>
        </Row>
        <Row>
          <Col>
            <FloatingLabel label="電話番号" className="mb-3">
              <Form.Control
                type="tel"
                value={lbUserDetails?.phoneNumber ?? ''}
                placeholder="012-3456-7890"
                readOnly
                onClick={() =>
                  editMode && redirectToLabBaseAuthPage('/user/profile', 'phone_number/update')
                }
                className={clsx(editMode && cursorPointer, !editMode && readOnlyMode)}
              />
            </FloatingLabel>
          </Col>
        </Row>
      </Card.Body>
      {editMode && (
        <Card.Footer>
          <Stack direction="horizontal" gap={3} className="justify-content-end">
            <Button variant="outline-secondary" onClick={() => setEditMode(false)}>
              キャンセル
            </Button>
            <Button
              variant="outline-primary"
              onClick={handleSubmit(
                (data) => userInfoMutation.mutate(data),
                (err) => console.log(err)
              )}
            >
              保存
            </Button>
          </Stack>
        </Card.Footer>
      )}
    </Card>
  )
}
